import { GridRenderCellParams, GridValidRowModel } from '@mui/x-data-grid-premium';
import { useMemo } from 'react';
import seedrandom from 'seedrandom';
import Skeleton from 'src/components/atoms/Skeleton';
import { AgButton } from 'src/components/ui';

import { DataGrid, DataGridProps, GridColObj } from 'src/modules/grids';

/** What the skeleton fabricates in place of rows — shimmer cells only ever read `id`. */
type SkeletonRow = { id: string | number };

export type SkeletonDataGridProps<T extends GridValidRowModel = GridValidRowModel> = Omit<
  DataGridProps<SkeletonRow>,
  'rows' | 'columns'
> & {
  columns: GridColObj<T>;
  rowCount: number;
  enterNewText?: string;
  DataGridComponent?: React.ComponentType<DataGridProps<SkeletonRow>>;
  sampleRowCreator?: (i: number) => SkeletonRow;
  idGetter?: (i: number) => string | number;
};

const SkeletonCell = ({ colDef, widths, rowNode }: GridRenderCellParams & { widths?: number[] }) => {
  const { type, minWidth = 100 } = colDef;
  const randWidth = useMemo(() => {
    const partialWidth = minWidth / (type === 'number' ? 3.5 : 2);
    const restWidth = minWidth - partialWidth;
    const randomWidth = widths[rowNode.id];

    return randomWidth * partialWidth + restWidth * 0.9;
  }, [minWidth, type, rowNode, widths]);

  if (type === 'actions') {
    return <Skeleton width={20} height={20} variant={'circular'} />;
  }

  return <Skeleton width={randWidth} variant={'text'} />;
};

const withSkeletonColumnCells = <T extends GridValidRowModel = GridValidRowModel>(
  columns: GridColObj<T>,
  rows: number,
  idGetter = (i) => i
): GridColObj<SkeletonRow> => {
  const keys = Object.keys(columns);
  const rng = seedrandom(keys[0]);
  const skeletonColumns = keys.map((key) => {
    const column = columns[key];
    const widths = {};

    for (let i = 0; i < rows; i++) {
      widths[idGetter(i)] = rng();
    }

    return {
      [key]: {
        ...column,
        renderHeader: (params) => (column.renderHeader ? <Skeleton>{column.renderHeader(params)}</Skeleton> : null),
        renderCell: (params) => <SkeletonCell {...params} widths={widths} />,
      },
    };
  });

  return Object.assign({}, ...skeletonColumns);
};

export default function SkeletonDataGrid<T extends GridValidRowModel = GridValidRowModel>({
  columns,
  enterNewText,
  rowCount,
  DataGridComponent = DataGrid,
  groupingColDef,
  sampleRowCreator = (i) => ({ id: i }),
  idGetter,
  ...props
}: SkeletonDataGridProps<T>) {
  const skeletonized = useMemo(
    () => withSkeletonColumnCells(columns, rowCount, idGetter),
    [columns, rowCount, idGetter]
  );

  const rows: SkeletonRow[] = [];
  const groupingColumn = typeof groupingColDef === 'function' ? undefined : groupingColDef;

  for (let i = 0; i < rowCount; i++) {
    rows.push(sampleRowCreator(i));
  }

  return (
    <>
      <DataGridComponent
        {...props}
        columns={skeletonized}
        sortModel={[]}
        rows={rows}
        groupingColDef={{
          ...groupingColumn,
          renderHeader: (params) =>
            groupingColumn?.renderHeader ? <Skeleton>{groupingColumn?.renderHeader(params)}</Skeleton> : null,
          renderCell: ({ field, colDef }) =>
            groupingColumn?.renderHeader ? (
              <Skeleton>{groupingColumn?.renderHeader({ field, colDef })}</Skeleton>
            ) : null,
        }}
      />
      {enterNewText && (
        <Skeleton sx={{ ml: 1 }}>
          <AgButton variant="primary">{enterNewText}</AgButton>
        </Skeleton>
      )}
    </>
  );
}
